/**
 * AÇIKLAMA BALONU — uzun gerekçeyi ekrandan kaldırır, erişimden KALDIRMAZ.
 *
 * Rapor ekranının sorunu şuydu: her damganın gerekçesi ve her tutarın dayanak
 * dökümü satır satır ekranda duruyordu; tek tek hepsi gerekli, hepsi bir arada
 * okunaksızdı. Çözüm gerekçeyi silmek değil, ikinci bir katmana almak.
 *
 * DÖRT KURAL, DÖRDÜ DE ZORUNLU:
 *
 * 1. YALNIZCA HOVER OLMAZ. Fare ile açılan bir açıklama, dokunmatik ekranda ve
 *    klavyeyle gezen kullanıcıda HİÇ açılmaz. Bu yüzden tetikleyici gerçek bir
 *    <button>: tıklamayla da açılır, sekme ile odaklanınca da. Fare ile açılan
 *    balon fare çekilince kapanır; tıklamayla açılan İĞNELENİR ve kullanıcı
 *    kapatana kadar durur — uzun bir dayanak dökümünü okurken farenin
 *    kaymasıyla kaybolmasın diye.
 * 2. YARDIMCI TEKNOLOJİYE BAĞLANIR. Balon `role="tooltip"` ve tetikleyici
 *    `aria-describedby` ile ona işaret ediyor; ekran okuyucu gerekçeyi
 *    tetikleyicinin AÇIKLAMASI olarak okur, kopuk bir metin olarak değil.
 * 3. BALON GÖVDEYE ÇİZİLİR — kapsayıcıya DEĞİL.
 *
 *    BULUNAN KUSUR: balon mutlak konumluyken tablo satırının içinde çiziliyordu
 *    ve tablonun `overflow-x-auto` / `overflow-hidden` sarmalları onu KIRPIYORDU.
 *    Kullanıcının gördüğü şey "balonlar yalnızca ayrıntı paneli açıkken
 *    görünüyor"du; sebebi balonun panelde olması değil, panel dışında kalan
 *    her yerde kırpılmasıydı. Kırpan kapsayıcı bir tasarım tercihi (yatay
 *    kaydırılan geniş tablo) olduğu için çözüm sarmalı açmak değil, balonu
 *    ondan ÇIKARMAK: `createPortal` ile `document.body`ye, `position: fixed`
 *    ile tetikleyicinin ekran koordinatına.
 * 4. KONUM EKRANIN İÇİNDE KALIR. Aşağıda yer yoksa yukarı çevriliyor, sağa
 *    taşarsa içeri çekiliyor. Açıkken kaydırma/yeniden boyutlandırma olursa
 *    balon tetikleyicisinin peşinden gidiyor — `fixed` olduğu için kendi
 *    başına gitmez ve tetikleyiciden kopmuş bir balon, yanlış sayıya ait
 *    gerekçeyi doğru sayının yanında gösterirdi.
 *
 * Tetikleyicinin GÖRÜNÜŞÜ burada dayatılmıyor: bir anormalliği işaretlemek
 * (kesikli alt çizgi, uyarı rengi) çağıranın kararı, çünkü hangi bilginin
 * anormal olduğunu yalnızca o bilir. Varsayılan `cursor: help`.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./primitives";

export interface AciklamaProps {
  /** Ekranda kalan kısa metin — tıklanabilir ve odaklanabilir olan bu. */
  readonly children: ReactNode;
  /** Balonun içeriği. Blok öğe KULLANMAYIN: balon bir <span>. */
  readonly icerik: ReactNode;
  /** Ekran okuyucunun tetikleyiciyi tanıması için, metin yetmiyorsa. */
  readonly label?: string;
  readonly className?: string | undefined;
}

interface Konum {
  readonly top: number;
  readonly left: number;
}

/** Balonun ekran içinde kalacağı pay — kenara yapışmasın. */
const KENAR = 8;
const ARALIK = 4;
/** Ölçüm yapılamadan önceki varsayım; ilk çizimden sonra gerçeğiyle değişir. */
const TAHMINI_YUKSEKLIK = 72;
const TAHMINI_GENISLIK = 280;

/** Bundan fazla kayan basış dokunuş değil kaydırmadır. */
const DOKUNUS_KAYMA_PX = 10;

/** Açık balonun kapatıcısı — aynı anda tek balon. */
let acikBalonuKapat: (() => void) | null = null;

/**
 * Odak bir dokunuşun/tıklamanın sonucu mu? Odakla açılma yalnızca klavyeyle
 * (ya da ekran okuyucuyla) gelene: fare ve dokunuş da odak verebilir
 * (masaüstünde tıklanan düğme odaklanır) ve o zaman tıklamanın kendi
 * aç/kapa kuralı yeter. `:focus-visible` her motorda aynı çalışmadığı için
 * işaretin basılı olduğu aralığı kendimiz izliyoruz.
 */
let isaretBasili = false;
if (typeof document !== "undefined") {
  // Fare/parmak odağı basma ile tıklama arasında olur; tıklamadan sonra
  // gelen odak (klavye, ekran okuyucu, program) işaretten değildir.
  const birak = (): void => {
    setTimeout(() => (isaretBasili = false), 0);
  };
  document.addEventListener("pointerdown", () => (isaretBasili = true), true);
  document.addEventListener("click", birak, true);
  document.addEventListener("pointercancel", birak, true);
  document.addEventListener("keydown", () => (isaretBasili = false), true);
}
function isaretOdagiMi(): boolean {
  return isaretBasili;
}

export function Aciklama({ children, icerik, label, className }: AciklamaProps) {
  const [uzerinde, setUzerinde] = useState(false);
  const [ignelenmis, setIgnelenmis] = useState(false);
  const [konum, setKonum] = useState<Konum | null>(null);
  const tetikRef = useRef<HTMLButtonElement | null>(null);
  const balonRef = useRef<HTMLSpanElement | null>(null);
  const id = useId();
  const acik = uzerinde || ignelenmis;

  const konumla = useCallback(() => {
    const tetik = tetikRef.current;
    if (tetik === null) return;
    const r = tetik.getBoundingClientRect();
    const balon = balonRef.current?.getBoundingClientRect();
    const h = balon?.height === undefined || balon.height === 0 ? TAHMINI_YUKSEKLIK : balon.height;
    const w = balon?.width === undefined || balon.width === 0 ? TAHMINI_GENISLIK : balon.width;
    // jsdom'da (ve ilk çizimde) tüm ölçüler 0 olabilir; `Math.max` sayesinde
    // sonuç yine geçerli bir konumdur ve test çökmez.
    const altBosluk = window.innerHeight - r.bottom;
    const yukari = altBosluk < h + ARALIK + KENAR && r.top > h + ARALIK + KENAR;
    const top = yukari ? r.top - h - ARALIK : r.bottom + ARALIK;
    const enSag = Math.max(KENAR, window.innerWidth - w - KENAR);
    setKonum({ top: Math.max(KENAR, top), left: Math.min(Math.max(KENAR, r.left), enSag) });
  }, []);

  useLayoutEffect(() => {
    if (!acik) {
      setKonum(null);
      return;
    }
    konumla();
  }, [acik, konumla]);

  useEffect(() => {
    if (!acik) return;
    // `capture`: kaydırma tablonun İÇ sarmalında olabilir ve iç kaydırmalar
    // pencereye baloncuklanmaz.
    const el = () => konumla();
    window.addEventListener("scroll", el, true);
    window.addEventListener("resize", el);
    return () => {
      window.removeEventListener("scroll", el, true);
      window.removeEventListener("resize", el);
    };
  }, [acik, konumla]);

  const kapat = useCallback(() => {
    setUzerinde(false);
    setIgnelenmis(false);
  }, []);

  // AYNI ANDA TEK BALON (kullanıcı, 20.10.2026): bu açılınca öbürü kapanır.
  // DOKUNUŞ KAPATIR, KAYDIRMA KAPATMAZ (kullanıcı kararı 21.10.2026): balonun
  // içine ya da dışına yapılan bir DOKUNUŞ (basıp yerinde bırakmak) balonu
  // kapatır; parmak kayarsa (kaydırma) dokunuş sayılmaz. Tetikleyicinin
  // kendisi hariç — onu kendi tıklaması aç/kapa yapar.
  useEffect(() => {
    if (!acik) return undefined;
    if (acikBalonuKapat !== null && acikBalonuKapat !== kapat) acikBalonuKapat();
    acikBalonuKapat = kapat;
    let basis: { id: number; x: number; y: number } | null = null;
    const bas = (e: PointerEvent): void => {
      basis = { id: e.pointerId, x: e.clientX, y: e.clientY };
    };
    const birak = (e: PointerEvent): void => {
      const b = basis;
      basis = null;
      if (b === null || b.id !== e.pointerId) return;
      if (Math.hypot(e.clientX - b.x, e.clientY - b.y) > DOKUNUS_KAYMA_PX) return;
      const hedef = e.target as Node | null;
      if (hedef !== null && tetikRef.current?.contains(hedef)) return;
      kapat();
    };
    const iptal = (): void => {
      basis = null;
    };
    document.addEventListener("pointerdown", bas, true);
    document.addEventListener("pointerup", birak, true);
    document.addEventListener("pointercancel", iptal, true);
    return () => {
      document.removeEventListener("pointerdown", bas, true);
      document.removeEventListener("pointerup", birak, true);
      document.removeEventListener("pointercancel", iptal, true);
      if (acikBalonuKapat === kapat) acikBalonuKapat = null;
    };
  }, [acik, kapat]);

  return (
    <>
      <button
        ref={tetikRef}
        type="button"
        className={cx("pf-aciklama", className)}
        aria-expanded={acik}
        {...(acik ? { "aria-describedby": id } : {})}
        {...(label === undefined ? {} : { "aria-label": label })}
        // FARE İLE PARMAK AYRI (kullanıcı, 20.10.2026 — iOS'ta dokunuş hem
        // "üstüne gelme" hem tıklama üretiyor, sırası tutarsız; üstüne gelince
        // içerik değişirse iOS ilk dokunuşun tıklamasını yutuyor). Üstüne
        // gelince açılma YALNIZCA farede; dokunmatikte tek kural: dokun aç,
        // tekrar dokun kapat.
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setUzerinde(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setUzerinde(false);
        }}
        onClick={() => setIgnelenmis((v) => !v)}
        // Odakla açılma yalnızca klavyeyle/ekran okuyucuyla gelene (fare/dokunuş odağı değil).
        onFocus={() => {
          if (!isaretOdagiMi()) setUzerinde(true);
        }}
        // Odak BAŞKA bir öğeye geçtiyse (klavyeyle gezinme) kapan; odak hiçbir
        // yere gitmediyse (balonun metnine dokunmak/tıklamak) iğnelenmiş balon
        // kalır — dışarı dokunuşu yukarıdaki dinleyici kapatır.
        onBlur={(e) => {
          if (e.relatedTarget !== null) kapat();
          else setUzerinde(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") kapat();
        }}
      >
        {children}
      </button>
      {acik &&
        createPortal(
          <span
            ref={balonRef}
            role="tooltip"
            id={id}
            className="pf-aciklama-balon"
            style={konum === null ? { visibility: "hidden" } : { top: konum.top, left: konum.left }}
          >
            {icerik}
          </span>,
          document.body,
        )}
    </>
  );
}
